/**
 * OIMS v2 - Approvals module (Maker/Checker)
 * Manages approval requests, pending queue, and real-time SSE updates.
 *
 * Security:
 * - C-1: All server content rendered through escapeHtml()
 * - Self-approval blocked server-side (sub comparison)
 */

import { escapeHtml } from './utils.js';

let state = null;
let pendingRequests = [];
let myRequests = [];
let pollTimer = null;
const POLL_INTERVAL = 30000; // 30s fallback poll (SSE is primary)

// My Requests pagination
let myReqPage = 0;
const MY_REQ_PER_PAGE = 5;

// Background RBAC sync (debounced after SSE updates)
let bgRefreshTimer = null;
const BG_REFRESH_DELAY = 2000; // 2s debounce

// DOM refs
let approvalBadge = null;
let btnMyReqPrev = null;
let btnMyReqNext = null;
let approvalMyPageInfo = null;
let approvalMyPagination = null;

export function initApprovals(appState) {
  state = appState;
  approvalBadge = document.getElementById('approvalBadge');
  btnMyReqPrev = document.getElementById('btnMyReqPrev');
  btnMyReqNext = document.getElementById('btnMyReqNext');
  approvalMyPageInfo = document.getElementById('approvalMyPageInfo');
  approvalMyPagination = document.getElementById('approvalMyPagination');

  // My Requests pagination buttons
  if (btnMyReqPrev) btnMyReqPrev.addEventListener('click', () => {
    if (myReqPage > 0) { myReqPage--; _renderMyRequestsPanel(); }
  });
  if (btnMyReqNext) btnMyReqNext.addEventListener('click', () => {
    const totalPages = Math.ceil(myRequests.length / MY_REQ_PER_PAGE);
    if (myReqPage < totalPages - 1) { myReqPage++; _renderMyRequestsPanel(); }
  });

  // Approval queue toggle (collapse/expand)
  const toggleEl = document.getElementById('approvalQueueToggle');
  const sectionEl = document.getElementById('approvalQueueSection');
  if (toggleEl && sectionEl) {
    toggleEl.addEventListener('click', () => {
      sectionEl.classList.toggle('collapsed');
    });
  }

  // Approval sub-tab switching
  document.querySelectorAll('.approval-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.approval-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      const target = tab.dataset.approvalTab;
      document.getElementById('approvalTabPending').style.display = target === 'pending' ? '' : 'none';
      document.getElementById('approvalTabMy').style.display = target === 'my' ? '' : 'none';
    });
  });

  // Listen for SSE approval_update events dispatched by notifications module
  document.addEventListener('oims:approval_update', (e) => {
    _handleApprovalEvent(e.detail);
  });

  // Listen for deep link: ?approval=<request_id>
  _checkDeepLink();

  // Initial load
  refreshPending();
  refreshMyRequests();

  // Fallback poll (SSE is primary channel)
  pollTimer = setInterval(() => {
    refreshPending();
    refreshMyRequests();
  }, POLL_INTERVAL);
}

export function cleanupApprovals() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (bgRefreshTimer) {
    clearTimeout(bgRefreshTimer);
    bgRefreshTimer = null;
  }
}

/**
 * Called by actions.js when execute returns status="approval_required".
 */
export function showApprovalRequested(data) {
  const requestId = data.request_id;
  const actionLabel = data.action || data.action_id || 'Action';
  const msg = data.message || 'Approval required. Your request has been submitted.';

  if (window.toast) {
    window.toast.info(msg);
  }

  // Refresh my requests to show the new pending one
  refreshMyRequests();
  refreshPending();
}

/**
 * Fetch pending approval requests visible to current user.
 */
export async function refreshPending() {
  if (!state || !state.accessToken) return;
  try {
    const { data } = await axios.get('/approvals/pending');
    pendingRequests = data.requests || [];
    _updateBadge();
    _renderPendingPanel();
  } catch (e) {
    console.debug('Failed to fetch pending approvals:', e);
  }
}

/**
 * Fetch current user's own requests.
 */
export async function refreshMyRequests() {
  if (!state || !state.accessToken) return;
  try {
    const { data } = await axios.get('/approvals/my-requests');
    myRequests = data.requests || [];
    myReqPage = 0;
    _renderMyRequestsPanel();
  } catch (e) {
    console.debug('Failed to fetch my requests:', e);
  }
}

/**
 * Approve a pending request.
 */
async function _approveRequest(requestId) {
  try {
    const { data } = await axios.post(`/approvals/${requestId}/approve`);
    if (data.status === 'executing' || data.status === 'completed') {
      window.toast?.success(data.status === 'completed' ? 'Approved and completed.' : 'Approved and executing.');

      // If command_id returned, offer to switch to Actions tab and stream
      if (data.command_id && data.instance_id) {
        document.dispatchEvent(new CustomEvent('oims:approval-executed', {
          detail: {
            command_id: data.command_id,
            instance_id: data.instance_id,
            instance_name: data.instance_name,
            action_id: data.action_id,
            action_label: data.action_label,
            request_id: data.request_id,
          }
        }));
      }

      // For instant actions (e.g. IP whitelisting), dispatch approval_update
      if (data.status === 'completed') {
        document.dispatchEvent(new CustomEvent('oims:approval_update', {
          detail: { action_id: data.action_id, status: 'completed' }
        }));
      }
    } else {
      window.toast?.error('Approval failed: ' + (data.status || 'unknown'));
    }
    refreshPending();
    refreshMyRequests();
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    window.toast?.error('Approve failed: ' + msg);
  }
}

/**
 * Deny a pending request with optional reason.
 */
async function _denyRequest(requestId) {
  // Prompt for reason
  const reason = prompt('Denial reason (optional):');

  try {
    let url = `/approvals/${requestId}/deny`;
    if (reason) {
      url += `?reason=${encodeURIComponent(reason)}`;
    }
    await axios.post(url);
    window.toast?.info('Request denied.');
    refreshPending();
    refreshMyRequests();
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    window.toast?.error('Deny failed: ' + msg);
  }
}

/**
 * Cancel own pending request.
 */
async function _cancelRequest(requestId) {
  try {
    await axios.post(`/approvals/${requestId}/cancel`);
    window.toast?.info('Request cancelled.');
    refreshPending();
    refreshMyRequests();
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    window.toast?.error('Cancel failed: ' + msg);
  }
}

// ── SSE Event Handling ─────────────────────────────────────────

function _handleApprovalEvent(event) {
  // Real-time update from SSE — optimistic local update (no HTTP round-trip)
  const { status, request_id, action_label, instance_name, requester,
          approver, denial_reason, emergency_override,
          escalated, created_at, decided_at, timestamp } = event;

  // 1. Show toast notification
  if (status === 'pending') {
    window.toast?.info(`Approval needed: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'executing' && emergency_override) {
    window.toast?.warning(`EMERGENCY OVERRIDE: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'executing') {
    window.toast?.success(`Approved: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'denied') {
    window.toast?.warning(`Denied: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'expired') {
    window.toast?.warning(`Expired: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'cancelled') {
    window.toast?.info(`Cancelled: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'completed') {
    window.toast?.success(`Completed: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  } else if (status === 'failed') {
    window.toast?.error(`Failed: ${escapeHtml(action_label || '')} on ${escapeHtml(instance_name || '')}`);
  }

  // 2. Update pendingRequests in-place
  if (status === 'pending') {
    // New pending request — need RBAC fields from server, schedule fetch
    if (!pendingRequests.find(r => r.request_id === request_id)) {
      pendingRequests.unshift({
        request_id,
        status: 'pending',
        action_id: event.action_id,
        action_label,
        instance_id: event.instance_id || '',
        instance_name,
        requester_email: requester,
        created_at: created_at || Date.now() / 1000,
        escalated: escalated || false,
        can_approve: false,       // placeholder until RBAC refresh
        can_breakglass: false,    // placeholder until RBAC refresh
        breakglass_cooldown: 0,
      });
    }
  } else {
    // Status changed — remove from pending (no longer pending)
    const idx = pendingRequests.findIndex(r => r.request_id === request_id);
    if (idx >= 0) pendingRequests.splice(idx, 1);
  }

  // 3. Update myRequests in-place
  const myIdx = myRequests.findIndex(r => r.request_id === request_id);
  if (myIdx >= 0) {
    // Only update if event is newer (prevent out-of-order overwrites)
    const existingTs = myRequests[myIdx]._ts || 0;
    if ((timestamp || 0) >= existingTs) {
      myRequests[myIdx].status = status;
      myRequests[myIdx].decided_at = decided_at || myRequests[myIdx].decided_at;
      myRequests[myIdx].approver_email = approver || myRequests[myIdx].approver_email;
      myRequests[myIdx].denial_reason = denial_reason || myRequests[myIdx].denial_reason;
      myRequests[myIdx]._ts = timestamp || Date.now() / 1000;
    }
  } else if (status === 'pending' && state && requester === state.email) {
    // New request that's mine — add to my list
    myRequests.unshift({
      request_id,
      status: 'pending',
      action_id: event.action_id,
      action_label,
      instance_name,
      created_at: created_at || Date.now() / 1000,
      decided_at: null,
      approver_email: null,
      denial_reason: null,
      _ts: timestamp || Date.now() / 1000,
    });
  }

  // 4. Immediate re-render (0ms delay)
  _updateBadge();
  _renderPendingPanel();
  _renderMyRequestsPanel();

  // 5. Schedule background RBAC refresh (debounced 2s)
  _scheduleBackgroundRefresh();
}

/**
 * Debounced background fetch to sync RBAC fields (can_approve, can_breakglass).
 * SSE events don't carry per-viewer RBAC state, so we fetch it lazily.
 */
function _scheduleBackgroundRefresh() {
  if (bgRefreshTimer) clearTimeout(bgRefreshTimer);
  bgRefreshTimer = setTimeout(() => {
    bgRefreshTimer = null;
    _backgroundRefreshPending();
  }, BG_REFRESH_DELAY);
}

async function _backgroundRefreshPending() {
  if (!state || !state.accessToken) return;
  try {
    const { data } = await axios.get('/approvals/pending');
    const fresh = data.requests || [];
    // Merge RBAC fields into existing local state (don't replace the array)
    const freshMap = new Map(fresh.map(r => [r.request_id, r]));

    // Update existing items with RBAC fields; remove stale ones
    pendingRequests = pendingRequests.filter(localReq => {
      const serverReq = freshMap.get(localReq.request_id);
      if (!serverReq) return false; // removed server-side
      localReq.can_approve = serverReq.can_approve;
      localReq.can_breakglass = serverReq.can_breakglass;
      localReq.breakglass_cooldown = serverReq.breakglass_cooldown;
      localReq.escalated = serverReq.escalated;
      return true;
    });

    // Add any server items we don't have locally (missed SSE events)
    for (const serverReq of fresh) {
      if (!pendingRequests.find(r => r.request_id === serverReq.request_id)) {
        pendingRequests.push(serverReq);
      }
    }

    _updateBadge();
    _renderPendingPanel();
  } catch (e) {
    console.debug('Background RBAC sync failed:', e);
  }
}

// ── Deep Link Handling ─────────────────────────────────────────

function _checkDeepLink() {
  const params = new URLSearchParams(window.location.search);
  const approvalId = params.get('approval');
  if (approvalId) {
    // Clean URL
    const url = new URL(window.location);
    url.searchParams.delete('approval');
    history.replaceState({}, '', url.pathname + url.search);

    // Show approval detail after a short delay for UI to be ready
    setTimeout(() => _showApprovalDetail(approvalId), 500);
  }
}

async function _showApprovalDetail(requestId) {
  try {
    const { data } = await axios.get(`/approvals/${requestId}`);
    _openApprovalModal(data);
  } catch (e) {
    window.toast?.error('Approval request not found.');
  }
}

// ── Rendering ──────────────────────────────────────────────────

function _updateBadge() {
  if (!approvalBadge) return;
  const count = pendingRequests.filter(r => r.can_approve).length;
  if (count > 0) {
    approvalBadge.textContent = count > 99 ? '99+' : String(count);
    approvalBadge.style.display = 'flex';
  } else {
    approvalBadge.style.display = 'none';
  }
}

function _renderPendingPanel() {
  const panel = document.getElementById('approvalPendingList');
  if (!panel) return;

  const emptyEl = document.getElementById('approvalPendingEmpty');

  if (pendingRequests.length === 0) {
    panel.innerHTML = '';
    if (emptyEl) emptyEl.style.display = 'block';
    return;
  }

  if (emptyEl) emptyEl.style.display = 'none';

  panel.innerHTML = pendingRequests.map(r => {
    const age = _formatAge(r.created_at);
    const escalatedBadge = r.escalated ? '<span class="approval-escalated">URGENT</span>' : '';
    const requesterShort = r.requester_email || 'Unknown';

    let actions = '';
    if (r.can_approve) {
      actions = `
        <div class="approval-actions">
          <button class="btn-approve" onclick="window._oims_approve('${escapeHtml(r.request_id)}')" title="Approve">
            <i class="bi bi-check-lg"></i> Approve
          </button>
          <button class="btn-deny" onclick="window._oims_deny('${escapeHtml(r.request_id)}')" title="Deny">
            <i class="bi bi-x-lg"></i> Deny
          </button>
        </div>`;
    } else {
      actions = '<span class="approval-waiting">Awaiting approver</span>';
    }

    let breakglassBtn = '';
    if (r.can_breakglass) {
      const cooldown = r.breakglass_cooldown || 0;
      if (cooldown > 0) {
        const cooldownMin = Math.ceil(cooldown / 60);
        breakglassBtn = `
          <div class="breakglass-row">
            <button class="btn-breakglass disabled" disabled title="Cooldown active">
              <i class="bi bi-hourglass-split"></i> Cooldown (${cooldownMin}m)
            </button>
          </div>`;
      } else {
        breakglassBtn = `
          <div class="breakglass-row">
            <button class="btn-breakglass" onclick="window._oims_breakglass('${escapeHtml(r.request_id)}')" title="Emergency Override">
              <i class="bi bi-shield-exclamation"></i> Emergency Override
            </button>
          </div>`;
      }
    }

    return `<div class="approval-card ${r.escalated ? 'escalated' : ''}">
      <div class="approval-card-header">
        <span class="approval-label">${escapeHtml(r.action_label)}</span>
        ${escalatedBadge}
      </div>
      <div class="approval-card-body">
        <div class="approval-detail"><i class="bi bi-hdd-rack"></i> ${escapeHtml(r.instance_name)}</div>
        <div class="approval-detail"><i class="bi bi-person"></i> ${escapeHtml(requesterShort)}</div>
        <div class="approval-detail"><i class="bi bi-clock"></i> ${escapeHtml(age)}</div>
      </div>
      ${actions}
      ${breakglassBtn}
    </div>`;
  }).join('');
}

function _renderMyRequestsPanel() {
  const panel = document.getElementById('approvalMyList');
  if (!panel) return;

  const emptyEl = document.getElementById('approvalMyEmpty');
  const total = myRequests.length;
  const totalPages = Math.ceil(total / MY_REQ_PER_PAGE) || 1;

  // Clamp page
  if (myReqPage >= totalPages) myReqPage = totalPages - 1;
  if (myReqPage < 0) myReqPage = 0;

  if (total === 0) {
    panel.innerHTML = '';
    if (emptyEl) emptyEl.style.display = 'block';
    if (approvalMyPagination) approvalMyPagination.style.display = 'none';
    return;
  }

  if (emptyEl) emptyEl.style.display = 'none';

  // Pagination controls
  if (approvalMyPagination) {
    approvalMyPagination.style.display = total > MY_REQ_PER_PAGE ? 'flex' : 'none';
  }
  if (approvalMyPageInfo) {
    approvalMyPageInfo.textContent = `Page ${myReqPage + 1} of ${totalPages} (${total} requests)`;
  }
  if (btnMyReqPrev) btnMyReqPrev.disabled = myReqPage === 0;
  if (btnMyReqNext) btnMyReqNext.disabled = myReqPage >= totalPages - 1;

  const start = myReqPage * MY_REQ_PER_PAGE;
  const pageItems = myRequests.slice(start, start + MY_REQ_PER_PAGE);

  panel.innerHTML = pageItems.map(r => {
    const age = _formatAge(r.created_at);
    const statusBadge = _statusBadge(r.status);

    let actions = '';
    if (r.status === 'pending') {
      actions = `<button class="btn-cancel-sm" onclick="window._oims_cancel('${escapeHtml(r.request_id)}')" title="Cancel">
        <i class="bi bi-x-circle"></i>
      </button>`;
    }

    const approverInfo = r.approver_email
      ? `<span class="approval-approver">${escapeHtml(r.approver_email)}</span>`
      : '';

    return `<div class="my-request-item">
      <div class="my-request-info">
        <span class="my-request-label">${escapeHtml(r.action_label)}</span>
        <span class="my-request-instance">${escapeHtml(r.instance_name)}</span>
      </div>
      <div class="my-request-meta">
        ${statusBadge} ${approverInfo}
        <span class="my-request-age">${escapeHtml(age)}</span>
        ${actions}
      </div>
    </div>`;
  }).join('');
}

function _statusBadge(status) {
  const map = {
    pending:   '<span class="approval-status pending"><i class="bi bi-hourglass-split"></i> Pending</span>',
    approved:  '<span class="approval-status approved"><i class="bi bi-check-circle"></i> Approved</span>',
    denied:    '<span class="approval-status denied"><i class="bi bi-x-circle"></i> Denied</span>',
    expired:   '<span class="approval-status expired"><i class="bi bi-clock-history"></i> Expired</span>',
    cancelled: '<span class="approval-status cancelled"><i class="bi bi-dash-circle"></i> Cancelled</span>',
    executing: '<span class="approval-status executing"><i class="bi bi-arrow-repeat"></i> Executing</span>',
    completed: '<span class="approval-status completed"><i class="bi bi-check-circle-fill"></i> Done</span>',
    failed:    '<span class="approval-status failed"><i class="bi bi-exclamation-triangle"></i> Failed</span>',
  };
  return map[status] || `<span class="approval-status">${escapeHtml(status)}</span>`;
}

function _openApprovalModal(data) {
  const modal = document.getElementById('approvalDetailModal');
  if (!modal) return;

  const el = (id, val) => {
    const e = document.getElementById(id);
    if (e) e.textContent = val || '-';
  };

  el('approvalDetailAction', data.action_label);
  el('approvalDetailInstance', data.instance_name);
  el('approvalDetailRequester', data.requester_email);
  el('approvalDetailStatus', data.status);
  el('approvalDetailCreated', data.created_at ? new Date(data.created_at * 1000).toLocaleString() : '-');

  const btnApprove = document.getElementById('btnApprovalDetailApprove');
  const btnDeny = document.getElementById('btnApprovalDetailDeny');

  if (data.can_approve && data.status === 'pending') {
    if (btnApprove) {
      btnApprove.style.display = '';
      btnApprove.onclick = () => {
        bootstrap.Modal.getInstance(modal)?.hide();
        _approveRequest(data.request_id);
      };
    }
    if (btnDeny) {
      btnDeny.style.display = '';
      btnDeny.onclick = () => {
        bootstrap.Modal.getInstance(modal)?.hide();
        _denyRequest(data.request_id);
      };
    }
  } else {
    if (btnApprove) btnApprove.style.display = 'none';
    if (btnDeny) btnDeny.style.display = 'none';
  }

  const bsModal = new bootstrap.Modal(modal);
  bsModal.show();
}

function _formatAge(ts) {
  if (!ts) return '';
  const diff = (Date.now() / 1000) - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

// ── Break-Glass Emergency Override ─────────────────────────────────

let breakglassModal = null;
let breakglassCountdown = null;
let breakglassTimer = null;

async function _initiateBreakglass(requestId) {
  // Step 1: Call initiate endpoint to generate OTP + send to Telegram
  try {
    const { data } = await axios.post(`/approvals/${requestId}/breakglass`);
    _showBreakglassModal(requestId, data);
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    window.toast?.error('Break-glass failed: ' + msg);
  }
}

function _showBreakglassModal(requestId, initData) {
  // Remove existing modal if any
  _closeBreakglassModal();

  const smsFallbackSec = initData.sms_fallback_seconds || 60;
  const otpTtl = initData.otp_ttl_seconds || 30;

  const overlay = document.createElement('div');
  overlay.className = 'breakglass-modal-overlay';
  overlay.id = 'breakglassOverlay';

  overlay.innerHTML = `
    <div class="breakglass-modal">
      <h3><i class="bi bi-shield-exclamation"></i> Emergency Override</h3>
      <p class="subtitle">OTP sent to Telegram. Enter code to execute immediately.</p>

      <div class="breakglass-field">
        <label>Reason for emergency override (min 10 chars)</label>
        <textarea id="bgReason" rows="2" maxlength="500" placeholder="Production is down, no second approver available..."></textarea>
      </div>

      <div class="breakglass-field">
        <label>One-Time Password</label>
        <input type="text" id="bgOtp" class="breakglass-otp-input"
               maxlength="6" placeholder="000000" autocomplete="off" inputmode="numeric">
      </div>

      <div class="breakglass-countdown">
        <span>OTP expires in</span>
        <span class="timer" id="bgTimer">${otpTtl}</span>
        <span>seconds</span>
      </div>

      <div class="breakglass-resend" id="bgResend" style="display:none;">
        <button class="btn-resend-otp" id="bgResendBtn" onclick="window._oims_bg_resend('${requestId}')">
          <i class="bi bi-arrow-clockwise"></i> Resend OTP to Telegram
        </button>
        <button class="btn-sms-fallback" id="bgSmsBtn" onclick="window._oims_bg_sms('${requestId}')">
          <i class="bi bi-phone"></i> Send via SMS instead
        </button>
      </div>

      <div class="breakglass-status" id="bgStatus"></div>

      <div class="breakglass-actions">
        <button class="btn-cancel" onclick="window._oims_bg_close()">Cancel</button>
        <button class="btn-submit" id="bgSubmit" onclick="window._oims_bg_verify('${requestId}')">
          <i class="bi bi-shield-check"></i> Verify &amp; Execute
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  breakglassModal = overlay;

  // Focus OTP input
  setTimeout(() => document.getElementById('bgOtp')?.focus(), 100);

  // Start OTP countdown timer
  let remaining = otpTtl;
  const timerEl = document.getElementById('bgTimer');
  breakglassTimer = setInterval(() => {
    remaining--;
    if (timerEl) timerEl.textContent = remaining;
    if (remaining <= 0) {
      clearInterval(breakglassTimer);
      breakglassTimer = null;
      if (timerEl) timerEl.textContent = 'expired';
      // Show resend + SMS buttons when OTP expires
      const resendEl = document.getElementById('bgResend');
      if (resendEl) resendEl.style.display = 'flex';
    }
  }, 1000);
}

function _closeBreakglassModal() {
  if (breakglassTimer) {
    clearInterval(breakglassTimer);
    breakglassTimer = null;
  }
  if (breakglassCountdown) {
    clearTimeout(breakglassCountdown);
    breakglassCountdown = null;
  }
  const overlay = document.getElementById('breakglassOverlay');
  if (overlay) overlay.remove();
  breakglassModal = null;
}

async function _verifyBreakglass(requestId) {
  const reason = document.getElementById('bgReason')?.value?.trim() || '';
  const otp = document.getElementById('bgOtp')?.value?.trim() || '';
  const statusEl = document.getElementById('bgStatus');
  const submitBtn = document.getElementById('bgSubmit');

  if (reason.length < 10) {
    if (statusEl) { statusEl.textContent = 'Reason must be at least 10 characters.'; statusEl.className = 'breakglass-status error'; }
    return;
  }
  if (otp.length !== 6) {
    if (statusEl) { statusEl.textContent = 'Enter the 6-digit OTP code.'; statusEl.className = 'breakglass-status error'; }
    return;
  }

  if (submitBtn) submitBtn.disabled = true;
  if (statusEl) { statusEl.textContent = 'Verifying...'; statusEl.className = 'breakglass-status'; }

  try {
    const { data } = await axios.post(`/approvals/${requestId}/breakglass/verify`, {
      otp,
      reason,
      method: 'telegram_otp',
    });

    if (data.status === 'executing' || data.status === 'completed') {
      window.toast?.success('Emergency override executed.');
      _closeBreakglassModal();

      if (data.command_id && data.instance_id) {
        document.dispatchEvent(new CustomEvent('oims:approval-executed', {
          detail: {
            command_id: data.command_id,
            instance_id: data.instance_id,
            instance_name: data.instance_name,
            action_id: data.action_id,
            action_label: data.action_label,
            request_id: data.request_id,
          }
        }));
      }

      // For instant actions (e.g. IP whitelisting), dispatch approval_update
      if (data.status === 'completed') {
        document.dispatchEvent(new CustomEvent('oims:approval_update', {
          detail: { action_id: data.action_id, status: 'completed' }
        }));
      }

      refreshPending();
      refreshMyRequests();
    } else {
      if (statusEl) { statusEl.textContent = 'Override failed: ' + (data.status || 'unknown'); statusEl.className = 'breakglass-status error'; }
    }
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    if (statusEl) { statusEl.textContent = msg; statusEl.className = 'breakglass-status error'; }
  } finally {
    if (submitBtn) submitBtn.disabled = false;
  }
}

async function _smsFallback(requestId) {
  const smsBtn = document.getElementById('bgSmsBtn');
  const statusEl = document.getElementById('bgStatus');
  if (smsBtn) smsBtn.disabled = true;

  try {
    const { data } = await axios.post(`/approvals/${requestId}/breakglass/sms-fallback`);
    if (statusEl) {
      statusEl.textContent = `SMS sent to ${data.phone_hint}. New code valid ${data.otp_ttl_seconds}s.`;
      statusEl.className = 'breakglass-status success';
    }
    // Reset timer for new OTP
    let remaining = data.otp_ttl_seconds || 30;
    const timerEl = document.getElementById('bgTimer');
    if (breakglassTimer) clearInterval(breakglassTimer);
    breakglassTimer = setInterval(() => {
      remaining--;
      if (timerEl) timerEl.textContent = remaining;
      if (remaining <= 0) {
        clearInterval(breakglassTimer);
        breakglassTimer = null;
        if (timerEl) timerEl.textContent = 'expired';
      }
    }, 1000);
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    if (statusEl) { statusEl.textContent = 'SMS failed: ' + msg; statusEl.className = 'breakglass-status error'; }
  } finally {
    if (smsBtn) smsBtn.disabled = false;
  }
}

async function _resendOtp(requestId) {
  const resendBtn = document.getElementById('bgResendBtn');
  const statusEl = document.getElementById('bgStatus');
  if (resendBtn) resendBtn.disabled = true;

  try {
    const { data } = await axios.post(`/approvals/${requestId}/breakglass`);
    if (statusEl) {
      statusEl.textContent = 'New OTP sent to Telegram.';
      statusEl.className = 'breakglass-status success';
    }
    // Hide resend buttons and restart timer
    const resendEl = document.getElementById('bgResend');
    if (resendEl) resendEl.style.display = 'none';

    let remaining = data.otp_ttl_seconds || 60;
    const timerEl = document.getElementById('bgTimer');
    if (breakglassTimer) clearInterval(breakglassTimer);
    breakglassTimer = setInterval(() => {
      remaining--;
      if (timerEl) timerEl.textContent = remaining;
      if (remaining <= 0) {
        clearInterval(breakglassTimer);
        breakglassTimer = null;
        if (timerEl) timerEl.textContent = 'expired';
        if (resendEl) resendEl.style.display = 'flex';
      }
    }, 1000);
  } catch (e) {
    const msg = e.response?.data?.detail || e.message;
    if (statusEl) { statusEl.textContent = msg; statusEl.className = 'breakglass-status error'; }
  } finally {
    if (resendBtn) resendBtn.disabled = false;
  }
}

// Global handlers for onclick in rendered HTML
window._oims_approve = _approveRequest;
window._oims_deny = _denyRequest;
window._oims_cancel = _cancelRequest;
window._oims_breakglass = _initiateBreakglass;
window._oims_bg_verify = _verifyBreakglass;
window._oims_bg_sms = _smsFallback;
window._oims_bg_resend = _resendOtp;
window._oims_bg_close = _closeBreakglassModal;
